import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssessmentRowComponent } from '../assessment-row/assessment-row.component';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { WnlGroupDirective } from '../wnl/wnl-group.directive';
import { WnlControlDirective } from '../wnl/wnl-control.directive';

import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { GastrointestinalFormGroup } from 'src/app/calvin-emr-shared/models/assessments/gastrointestinal.model';

@Component({
  selector: 'app-gastrointestinal-form',
  templateUrl: './gastrointestinal-form.component.html',
  styleUrls: ['./gastrointestinal-form.component.scss', '../assessments-tab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    AssessmentRowComponent,
    EmrInputComponent,
    EmrCheckboxComponent,
    WnlGroupDirective,
    WnlControlDirective,
  ],
})
export class GastrointestinalFormComponent {

  formGroup = input.required<GastrointestinalFormGroup>();
  patient = input.required<Patient>();
  @ViewChild(WnlGroupDirective) wnlGroup?: WnlGroupDirective;

  bowelSoundsOptions = [
    new FormOption('Hypoactive'),
    new FormOption('Hyperactive'),
    new FormOption('Absent'),
    new FormOption('WNL'),
  ];

  emesisOptions = [
    new FormOption('Small'),
    new FormOption('Medium'),
    new FormOption('Large'),
    new FormOption('WNL'),
  ];

  stomaSiteOptions = [
    new FormOption('Pink'),
    new FormOption('Beefy red'),
    new FormOption('Pale'),
    new FormOption('Purple'),
    new FormOption('None'),
  ];

  outputOptions = [
    new FormOption('Loose'),
    new FormOption('Watery'),
    new FormOption('Formed'),
  ];

  oralDietOptions = [
    new FormOption('Total Feed'),
    new FormOption('Partial Assist'),
    new FormOption('Tray set up'),
    new FormOption('Independent'),
  ];

  weightLossOptions = [
    new FormOption('< 5%'),
    new FormOption('5%-10%'),
    new FormOption('> 10%'),
  ]

  ngOnInit(): void {
    this.updateNutritionScore();

    this.formGroup().controls.tubeFeed.valueChanges.subscribe(() => {
      if (!this.formGroup().controls.tubeFeed.value) {
        this.formGroup().controls.tubeType.setValue('');
        this.formGroup().controls.bagChangedHours.setValue('');
        this.formGroup().controls.siteAssessment.setValue('');
      }
    });

    this.formGroup().controls.reducedAppetite.valueChanges.subscribe(() => {
      this.updateNutritionScore();
    });

    this.formGroup().controls.weightLossPercentage.valueChanges.subscribe(() => {
      this.updateNutritionScore();
    });
  }

  reset() {
    this.formGroup().enable({ emitEvent: false });
  }

  nutritionRiskIsWnl = (score: any): boolean => Number(score) === 0;

  private updateNutritionScore() {
    this.formGroup().controls.nutritionScore.setValue(`${this.nutritionScore}`, { emitEvent: false });
    this.wnlGroup?.notifyStateChanged();
  }

  get BMIScore() {
    const BMIScore = this.patient().getBMI();
    if (BMIScore > 20) {
      return 0;
    } else if (BMIScore > 18.5) {
      return 1;
    } else {
      return 3;
    }
  }

  get weightScore() {
    switch (this.formGroup().controls.weightLossPercentage.value) {
      case '> 10%':
        return 2;
      case '5%-10%':
        return 1;
      default:
        return 0
    }
  }

  get acuteDiseaseEffectScore() {
    return this.formGroup().controls.reducedAppetite.value ? 1 : 0;
  }

  get nutritionScore() {
    return (
      this.BMIScore +
      this.weightScore +
      this.acuteDiseaseEffectScore
    );
  }

  get nutritionRiskCategory(): string {
    const nutritionScore = this.nutritionScore;
    if (nutritionScore >= 2) {
      return 'High';
    } else if (nutritionScore === 1) {
      return 'Medium';
    } else {
      return 'Low';
    }
  }
}